import { useState } from 'react'

import { useBatches, useModules, usePrepareModules } from '@ctcells/api'
import type { Batch, ModuleSnapshot } from '@ctcells/contract'
import { errorMessage, t } from '@ctcells/i18n'

import { ACTIVE_BATCH_LIMIT } from '../../shell/activeBatchSource.ts'
import { gate, type ActionGate, type ScreenAccess } from '../access.ts'
import { batchDisplayName } from '../batches/batchDisplayName.ts'

/**
 * 배치 준비 강제 정리(`POST /modules/prepare`)의 **훅 · 게이트 단일 출처.**
 *
 * 두 자리가 같은 연산을 부른다 — 관리 › 설정의 「배치 준비 강제 정리」(`PrepareModulesContainer`)와
 * 모듈 서랍 「제어」 카드의 「트레이 자리 정리」(`ModulePrepareContainer` · eric 2026-09-28 「넣어줘」).
 * 🔴 **둘이 같은 훅 · 같은 게이트를 쓴다.** 한쪽만 막으면 다른 쪽이 구멍이 된다.
 *
 * ## 🔴 종착 안 한 작업이 하나라도 있으면 막는다 (2026-09-28)
 *
 * 서버는 그 작업들을 **전부 종착시킨다**(`module_prepare.prepare` ① — `created`·`queued`·`running` 은 `stop`,
 * `paused` 는 `recoverBatch discard`). 2026-09-27 에 eric 의 멈춘 작업이 이 연산에 조용히 폐기됐다.
 * 종전에는 확인 창 문구로만 막았다 — 이제 버튼이 **비활성 + 그 작업 이름**으로 선다. 사람이 먼저
 * 중지/폐기하면 풀린다. ⚠ 이 연산의 쓸모(트레이 자리 · 모듈 유휴)는 작업이 0건일 때도 그대로다.
 *
 * - 목록은 `GET /batches?phase=active`(상시 작업 줄과 **같은 질의 인자 = 같은 캐시**). 종착 판정은 서버가 한다
 * - 🔴 **못 읽었으면 막는다** — 「못 봤다」를 「0건」으로 접으면 있는 작업을 닫는다. 읽는 중도 같다
 * - ⚠ 그래서 게이트 재료인 작업 목록은 **대화가 닫혀 있어도 부른다**(종전 `skip: !open` 을 걷었다).
 *   되돌릴 모듈 목록은 대화 안에서만 쓰여 여전히 열렸을 때만 부른다
 */

/** 모듈 목록 한 번에 받는 상한. 서버 최대 500 아래로 둔다 */
const MODULE_LIMIT = 200

export interface PrepareBatchesState {
  activeBatches: readonly Batch[]
  activeBatchesLoading: boolean
  activeBatchesNotice?: string | undefined
}

/**
 * 「정리 실행」 버튼의 게이트. 차례: 보내는 중 → 보낼 곳 없음 → stale·권한·제어락(서버 403·409 와 짝) →
 * 작업 목록 읽는 중 · 못 읽음 → 종착 안 한 작업 있음.
 */
export function prepareRunGate(
  access: ScreenAccess,
  batches: PrepareBatchesState,
  { preparing, canSend }: { preparing: boolean; canSend: boolean },
): ActionGate {
  if (preparing) return { disabled: true, disabledReason: t('reason.requestInFlight') }
  if (!canSend) return { disabled: true, disabledReason: t('reason.demoSource') }
  const base = gate(access, { perm: 'control', needsLock: true })
  if (base.disabled) return base
  if (batches.activeBatchesNotice !== undefined)
    return {
      disabled: true,
      disabledReason: t('reason.prepareBatchesUnknown', { message: batches.activeBatchesNotice }),
    }
  if (batches.activeBatchesLoading)
    return { disabled: true, disabledReason: t('reason.prepareBatchesLoading') }
  if (batches.activeBatches.length > 0)
    return {
      disabled: true,
      disabledReason: t('reason.prepareActiveBatches', {
        names: batches.activeBatches.map((batch) => batchDisplayName(batch)).join(', '),
      }),
    }
  return { disabled: false }
}

export function usePrepareModulesFlow() {
  const [open, setOpen] = useState(false)
  const prepare = usePrepareModules()
  const batches = useBatches({ limit: ACTIVE_BATCH_LIMIT, phase: 'active' })
  const modules = useModules({ limit: MODULE_LIMIT, skip: !open })

  const batchesState: PrepareBatchesState = {
    activeBatches: batches.data?.items ?? [],
    activeBatchesLoading: batches.isPending,
    ...(batches.isError ? { activeBatchesNotice: errorMessage(batches.error) } : {}),
  }
  /*
   * 🔴 **`service_state` 로 거른다 — 그것이 서버가 쓰는 축이다**
   *    (`module_prepare.module_targets` → `module_endpoints(service_state=SERVICE_ACTIVE)`).
   * ⚠ **부재를 `active` 로 읽지 않는다**(계약 `ModuleSnapshot.service_state` 경고) — 실측 2026-09-22 에
   *   `GET /modules` 8건 중 `mock-*` 넷이 `terminated` 인데 서버는 넷만 건드렸다.
   */
  const targetModules: readonly ModuleSnapshot[] = (modules.data?.items ?? []).filter(
    (module) => module.service_state === 'active',
  )

  return {
    open,
    setOpen,
    prepare,
    refresh: async () => {
      const snapshots = await Promise.all([batches.refetch(), modules.refetch()])
      const failed = snapshots.find((snapshot) => snapshot.isError)
      if (failed) throw failed.error
    },
    batchesState,
    targetModules,
    modulesNotice: modules.isError ? errorMessage(modules.error) : undefined,
    error: prepare.isError ? errorMessage(prepare.error) : undefined,
    /** 🔴 `command_id` 는 훅이 만든다. 낙관적 갱신 없음 — 결과는 서버 응답 그대로다 */
    send: (reason: string) => prepare.mutate({ reason }),
  }
}
